import React, { useState } from "react";
import { GridApi, GridReadyEvent, RowNode, axiosInstance, useAuthStore, useSnackbar } from '@fides/fides-react-components';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getDocumentsColDefs } from '../../constants/DocumentsColDefs';
import { API_ENDPOINTS } from '../../constants/ApiEndpoints';
import { mapFilterModel, useDatasource } from '../../hooks/useDatasource';
import { Document } from '../../types/Document';
import { useFetchStaticData } from '../../api/hooks';
import { DLCDataGrid } from '../grid/DLCDataGrid';
import { checkCSAdmin, checkFidesAdmin, downloadReport, downloadZippedReports } from "../../api/helper";
import { CPS } from '../../constants/constant';

export const defaultColDef = {
  filter: 'agTextColumnFilter',
  filterParams: { filterOptions: ['contains'], suppressAndOrCondition: true, debounceMs: 1500 }
};

const DocumentOverview = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const [gridApi, setGridApi] = useState<GridApi>();

  const datasource = useDatasource<Document>({
    url: API_ENDPOINTS.documents.findAll
  }, t);

  const user = useAuthStore(state => state.user);
  const isFidesOrCSAdmin = checkFidesAdmin(user) || checkCSAdmin(user);

  const init = (api: GridApi): void => {
    setGridApi(api);
  };

  const rowActions = (currentNode: RowNode, api?: GridApi) => {
    const rowActionsArray: any = [];
    if (currentNode.data) {
      if (isFidesOrCSAdmin &&
        currentNode.data.application === CPS) {

        rowActionsArray.push({
          label: currentNode.data.active
            ? t("generic.inactivate")
            : t("generic.activate"),
          handleAction: () => {
            axiosInstance
              .put(`${API_ENDPOINTS.documents.activate}`, {
                uuids: [currentNode.data.uuid],
                active: !currentNode.data.active,
              })
              .then((response: any) => {
                api?.deselectAll();
                api?.refreshServerSideStore({ purge: true });
              });
          },
        });
      }

      if ((isFidesOrCSAdmin &&
        currentNode.data.application === CPS &&
        !currentNode.data.active) ||
        currentNode.data.application !== CPS) {

        rowActionsArray.push({
          label: t("generic.delete"),
          handleAction: () => {
            axiosInstance
              .delete(
                `${API_ENDPOINTS.documents.delete}?uuid=${currentNode.data.uuid}`
              )
              .then((response: any) => {
                api?.deselectAll();
                api?.refreshServerSideStore({ purge: true });
              });
          },
        });
      }

    }

    return rowActionsArray
  };

  const onCellClickedCall = (event: any) => {
    if (!!event.value) {
      history.push(`/viewReport/${event.data.uuid}`);
    }
  }

  const selectedRowsActions = (selectedNodes: RowNode[], api?: GridApi) => {
    let actions: any = [];

    const activeDocuments = selectedNodes.filter(node => node.data.active);
    const inActiveDocuments = selectedNodes.filter(node => !node.data.active);
    const cpsAppCheck = selectedNodes.filter(node => node.data.application === CPS);

    if (isFidesOrCSAdmin &&
      cpsAppCheck.length !== 0 && activeDocuments.length !== 0 && inActiveDocuments.length === 0) {
      actions = [
        ...actions,
        {
          label: t("generic.inactivate"),
          handleAction: () => {
            axiosInstance
              .put(`${API_ENDPOINTS.documents.activate}`, {
                uuids: activeDocuments.map(doc => doc.data.uuid),
                active: false
              })
              .then((response: any) => {
                api?.deselectAll();
                api?.refreshServerSideStore({ purge: true });
              });
          }
        }
      ]
    }

    if (isFidesOrCSAdmin &&
      cpsAppCheck.length !== 0 && inActiveDocuments.length !== 0 && activeDocuments.length === 0) {
      actions = [
        ...actions,
        {
          label: t("generic.activate"),
          handleAction: () => {
            axiosInstance
              .put(`${API_ENDPOINTS.documents.activate}`, {
                uuids: inActiveDocuments.map(doc => doc.data.uuid),
                active: true
              })
              .then((response: any) => {
                api?.deselectAll();
                api?.refreshServerSideStore({ purge: true });
              });
          }
        }
      ]
    }
    if ((isFidesOrCSAdmin &&
      cpsAppCheck.length !== 0 && inActiveDocuments.length !== 0) || cpsAppCheck.length === 0) {
      actions = [
        ...actions,
        {
          label: t("generic.delete"),
          handleAction: () => {
            let uuids = selectedNodes.map(node => node.data.uuid);
            axiosInstance
              .put(`${API_ENDPOINTS.documents.delete}`, { uuids: uuids })
              .then((response: any) => {
                api?.deselectAll();
                api?.refreshServerSideStore({ purge: true });
                if (activeDocuments.length !== 0 && cpsAppCheck.length !== 0) {
                  snackbar(t('document.onlyInactiveDeleted'), 'info');
                }
              });
          },
        }];
    }

    return actions;
  }

  const staticData = useFetchStaticData();

  const download = (selectedNodes: RowNode[], isAllSelected: boolean, api?: GridApi) => {
    if (selectedNodes.length === 1) {
      const row = selectedNodes[0].data;
      downloadReport(row.uuid, row.fileName).then(() => {
        api?.deselectAll();
        api?.refreshServerSideStore({ purge: true });
      });
    } else {
      const filterModel = gridApi?.getFilterModel();
      const filter = isAllSelected ? mapFilterModel(filterModel, t, gridApi?.getColumnDefs()) : null;
      const selectedItems = isAllSelected ? null : selectedNodes.map(n => n.data.uuid);

      downloadZippedReports(selectedItems, filter).then(() => {
        api?.deselectAll();
        api?.refreshServerSideStore({ purge: true });
      })
        .catch((reason) => {
          //error is returned as blob
          reason.response.data.text()
            .then((err: any) => {
              const error = JSON.parse(err);
              const fileSize = error.args[0];
              const maxSize = error.args[1];
              snackbar(t("document.zipError", { fileSize, maxSize }), "error");
            });
        });
    }
  };

  return (
    <DLCDataGrid
      gridId="dlcDocumentOverviewGrid"
      defaultColDef={defaultColDef}
      columnDefs={getDocumentsColDefs(t, staticData, isFidesOrCSAdmin)}
      datasource={datasource}
      sideBar={true}
      selectedRowsActions={selectedRowsActions}
      rowActions={rowActions}
      showFilterRowInitially={true}
      onCellClicked={onCellClickedCall}
      export={{
        url: API_ENDPOINTS.documents.findAll,
        fileName: 'exportReportOverviews',
        translation: { true: 'generic.active', false: 'generic.inactive' }
      }}
      onReport={download}
      onGridReady={(e: GridReadyEvent) => {
        init(e.api);
      }}
    />
  )
}

export default DocumentOverview;